import type { PreviewViewV1 } from "@enrein/shared-contracts/approval-change";
import { PreviewViewV1Schema } from "@enrein/shared-contracts/approval-change";
import { ClientIntakeViewV1Schema, type ClientIntakeViewV1 } from "@enrein/shared-contracts";
import { ClientPortalShellViewV1Schema, type ClientPortalShellViewV1 } from "@enrein/shared-contracts/client-link";
import type { ClientPortalApiClient } from "../../../api.js";
import type { V10ClientLinkViewProps } from "../types.js";

const DEV_FIXTURE_NOW = "2026-06-24T12:00:00.000Z";
const DEV_FIXTURE_LINK_ID = "dev-visual-fixture-link";
const DEV_FIXTURE_INTAKE_LINK_ID = "dev-intake-fixture-link";
const DEV_FIXTURE_SESSION_TOKEN = "dev-fixture-session-token-for-visual-review-only-00";
const DEV_FIXTURE_ACCESS_TOKEN = "dev-fixture-access-token-for-visual-review-only-000";
const DEV_FIXTURE_PREVIEW_VERSION_ID = "a".repeat(64);

function devFixtureApiBlocked(): never {
  throw new Error("dev_fixture_no_api");
}

const DEV_FIXTURE_INTAKE_VIEW: ClientIntakeViewV1 = ClientIntakeViewV1Schema.parse({
  schemaVersion: "client-intake-view.v1.0",
  clientLinkId: DEV_FIXTURE_INTAKE_LINK_ID,
  agencyId: "agency_dev_fixture",
  projectId: "project_dev_fixture",
  status: "DRAFT",
  linkStatus: "INTAKE_OPEN",
  businessName: "Your business",
  primaryPhone: "",
  primaryEmail: "",
  description: "",
  services: [],
  serviceAreas: [],
  evidenceFiles: [],
  submittedAt: null,
  updatedAt: DEV_FIXTURE_NOW,
});

export const V10_CLIENT_LINK_DEV_FIXTURE_API: ClientPortalApiClient = {
  createSession: async () => devFixtureApiBlocked(),
  getShell: async () => devFixtureApiBlocked(),
  getPreview: async () => devFixtureApiBlocked(),
  getJourneyPresentation: async () => devFixtureApiBlocked(),
  approve: async () => devFixtureApiBlocked(),
  requestChange: async () => devFixtureApiBlocked(),
  listChangeRequests: async () => devFixtureApiBlocked(),
  submitSafeEditPatch: async () => devFixtureApiBlocked(),
  getSafeEditOutcome: async () => devFixtureApiBlocked(),
  getCurrentClientReport: async () => devFixtureApiBlocked(),
  getCurrentClientLaunchProof: async () => devFixtureApiBlocked(),
  getCurrentClientLaunchCertificate: async () => devFixtureApiBlocked(),
  getClientWebsiteOwnershipRecord: async () => devFixtureApiBlocked(),
  getHandoff: async () => devFixtureApiBlocked(),
  downloadHandoff: async () => devFixtureApiBlocked(),
  getGovernance: async () => devFixtureApiBlocked(),
  getIntake: async () => devFixtureApiBlocked(),
  submitIntake: async () => devFixtureApiBlocked(),
  saveIntakeDraft: async () => devFixtureApiBlocked(),
  getExternalProofSalesRoom: async () => devFixtureApiBlocked(),
  approveExternalProofSalesRoom: async () => devFixtureApiBlocked(),
  getExternalAfterFixProof: async () => devFixtureApiBlocked(),
  getProjectRecordCenter: async () => devFixtureApiBlocked(),
  listClarifications: async () => devFixtureApiBlocked(),
  respondToClarification: async () => devFixtureApiBlocked(),
};

export const V10_CLIENT_INTAKE_DEV_FIXTURE_API: ClientPortalApiClient = {
  ...V10_CLIENT_LINK_DEV_FIXTURE_API,
  async getIntake() {
    return DEV_FIXTURE_INTAKE_VIEW;
  },
  async submitIntake(_clientLinkId, _token, payload) {
    const intake = ClientIntakeViewV1Schema.parse({
      ...DEV_FIXTURE_INTAKE_VIEW,
      businessName: payload.businessName,
      primaryPhone: payload.primaryPhone,
      primaryEmail: payload.primaryEmail,
      description: payload.description,
      services: payload.services,
      serviceAreas: payload.serviceAreas,
      primaryEvidenceType: payload.primaryEvidenceType,
      evidenceFiles: payload.evidenceFiles ?? [],
      status: "SUBMITTED",
      linkStatus: "INTAKE_SUBMITTED",
      submittedAt: DEV_FIXTURE_NOW,
      updatedAt: DEV_FIXTURE_NOW,
    });
    return {
      schemaVersion: "client-intake-submit-result.v1.0",
      intake,
      linkStatus: "INTAKE_SUBMITTED",
    };
  },
  async saveIntakeDraft(_clientLinkId, _token, payload) {
    const intake = ClientIntakeViewV1Schema.parse({
      ...DEV_FIXTURE_INTAKE_VIEW,
      businessName: payload.businessName ?? DEV_FIXTURE_INTAKE_VIEW.businessName,
      primaryPhone: payload.primaryPhone,
      primaryEmail: payload.primaryEmail,
      description: payload.description,
      services: payload.services ?? [],
      serviceAreas: payload.serviceAreas ?? [],
      primaryEvidenceType: payload.primaryEvidenceType,
      evidenceFiles: payload.evidenceFiles ?? [],
      linkStatus: "INTAKE_IN_PROGRESS",
      updatedAt: DEV_FIXTURE_NOW,
    });
    return {
      schemaVersion: "client-intake-draft-save-result.v1.0",
      intake,
      linkStatus: "INTAKE_IN_PROGRESS",
    };
  },
};

const DEV_FIXTURE_INTAKE_SHELL: ClientPortalShellViewV1 = ClientPortalShellViewV1Schema.parse({
  schemaId: "client-portal.shellView.v1",
  clientLinkId: DEV_FIXTURE_INTAKE_LINK_ID,
  state: {
    schemaId: "client-link.state.v1",
    clientLinkId: DEV_FIXTURE_INTAKE_LINK_ID,
    status: "INTAKE_OPEN",
    expiresAt: null,
    updatedAt: DEV_FIXTURE_NOW,
  },
  project: {
    schemaId: "client-safe-project-view.v1",
    displayName: "Lakeside Auto",
    lifecycleStatus: "INTAKE_OPEN",
  },
  headline: "Tell us about your business.",
  subcopy: "Complete intake so your agency can start your website project.",
  sections: [
    {
      id: "intake",
      label: "Intake",
      description: "Share business basics with your agency.",
      state: "AVAILABLE",
    },
  ],
  projectWorkflow: "DEFAULT",
});

const DEV_FIXTURE_SHELL: ClientPortalShellViewV1 = ClientPortalShellViewV1Schema.parse({
  schemaId: "client-portal.shellView.v1",
  clientLinkId: DEV_FIXTURE_LINK_ID,
  state: {
    schemaId: "client-link.state.v1",
    clientLinkId: DEV_FIXTURE_LINK_ID,
    status: "PREVIEW_READY",
    expiresAt: null,
    updatedAt: DEV_FIXTURE_NOW,
  },
  project: {
    schemaId: "client-safe-project-view.v1",
    displayName: "Acme HVAC",
    lifecycleStatus: "PREVIEW_READY",
  },
  headline: "Your website preview is ready for review.",
  subcopy: "Walk through every Client Link tab locally before Phase 2 wiring.",
  sections: [
    {
      id: "preview",
      label: "Preview",
      description: "Review the agency-built website snapshot.",
      state: "AVAILABLE",
    },
    {
      id: "approval",
      label: "Approval",
      description: "Approve the preview or send one clear change request.",
      state: "AVAILABLE",
    },
    {
      id: "handoff",
      label: "Handoff",
      description: "Download the departure kit after launch proof is complete.",
      state: "LOCKED",
    },
  ],
  projectWorkflow: "DEFAULT",
});

const DEV_FIXTURE_PREVIEW: PreviewViewV1 = PreviewViewV1Schema.parse({
  schemaId: "client-portal.previewView.v1",
  clientLinkId: DEV_FIXTURE_LINK_ID,
  status: "PREVIEW_READY",
  displayName: "Acme HVAC",
  previewStatus: "READY",
  previewVersionId: DEV_FIXTURE_PREVIEW_VERSION_ID,
  previewRoute: "/",
  previewHtml: "<p>Dev fixture preview placeholder.</p>",
  updatedAt: DEV_FIXTURE_NOW,
});

export type V10ClientLinkDevFixtureBaseProps = Pick<
  V10ClientLinkViewProps,
  | "shell"
  | "preview"
  | "sessionToken"
  | "locationContext"
  | "externalProofView"
  | "afterFixProofSummary"
  | "isExternalProof"
  | "api"
>;

export function buildV10ClientLinkDevFixtureBaseProps(): V10ClientLinkDevFixtureBaseProps {
  return {
    shell: DEV_FIXTURE_SHELL,
    preview: DEV_FIXTURE_PREVIEW,
    sessionToken: DEV_FIXTURE_SESSION_TOKEN,
    locationContext: {
      clientLinkId: DEV_FIXTURE_LINK_ID,
      token: DEV_FIXTURE_ACCESS_TOKEN,
    },
    externalProofView: null,
    afterFixProofSummary: null,
    isExternalProof: false,
    api: V10_CLIENT_LINK_DEV_FIXTURE_API,
  };
}

export function buildV10ClientIntakeDevFixtureBaseProps(): V10ClientLinkDevFixtureBaseProps {
  return {
    shell: DEV_FIXTURE_INTAKE_SHELL,
    preview: null,
    sessionToken: DEV_FIXTURE_SESSION_TOKEN,
    locationContext: {
      clientLinkId: DEV_FIXTURE_INTAKE_LINK_ID,
      token: DEV_FIXTURE_ACCESS_TOKEN,
    },
    externalProofView: null,
    afterFixProofSummary: null,
    isExternalProof: false,
    api: V10_CLIENT_INTAKE_DEV_FIXTURE_API,
  };
}
